<template>
  <myDialog  :isDialog="isDialog" :title="isView ? '查看政策' : (isEdit ? '编辑政策' : '添加政策')" :width="'1200px'" @handleClose="handleClose">
    <template slot="content">
      <el-form :model="formData" :rules="rules" ref="form" label-width="180px">
        <!-- 政策标签 -->
        <el-form-item label="政策标签" prop="policyLabelList" required>
          <el-select
            v-model="selectedTagValues"
            placeholder="请选择标签"
            @change="handleTagSelectChange"
            filterable
            clearable
            multiple
            :disabled="isView"
            style="width: 100%;"
          >
            <el-option
              v-for="tag in availableTags"
              :key="tag.dictValue"
              :label="tag.dictLabel"
              :value="tag.dictValue"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <!-- 文件名称 -->
        <el-form-item label="文件名称" prop="fileName" required>
          <el-input v-model="formData.fileName" placeholder="请输入文件名称" maxlength="200" show-word-limit :disabled="isView"></el-input>
        </el-form-item>

        <!-- 文号 -->
        <el-form-item label="文号" prop="referenceNumber" required>
          <el-input v-model="formData.referenceNumber" placeholder="请输入文号" maxlength="100" :disabled="isView"></el-input>
        </el-form-item>

        <!-- 选择政策添加方式 -->
        <el-form-item label="选择政策添加方式" prop="addType" required>
          <el-radio-group v-model="formData.addType" :disabled="isView">
            <el-radio label="direct">直接输入内容</el-radio>
            <el-radio label="link">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 政策内容（仅直接输入内容时显示） -->
        <el-form-item v-if="formData.addType === 'direct'" label="政策内容" prop="policyContent" required>
          <wang-editor
            v-if="!isView"
            v-model="formData.policyContent"
            :height="400"
            placeholder="请输入政策内容..."
          />
          <div v-else class="view-content" v-html="formData.policyContent"></div>
        </el-form-item>

        <!-- 外部链接URL（仅外部链接时显示） -->
        <el-form-item v-if="formData.addType === 'link'" label="外部链接URL" prop="link" required>
          <el-input v-model="formData.link" placeholder="请输入链接地址" :disabled="isView"></el-input>
        </el-form-item>

        <!-- 附件上传 -->
        <el-form-item label="附件上传">
          <el-upload
            v-if="!isView"
            class="upload-demo"
            action="#"
            :file-list="fileList"
            :on-remove="handleRemove"
            :on-error="handleUploadError"
            :before-upload="beforeUpload"
            :http-request="handleCustomUpload"
            :auto-upload="true"
            :on-progress="handleProgress"
            multiple
          >
            <div class="upload-area">
              <i class="el-icon-upload" style="font-size: 48px; color: #c0c4cc;"></i>
              <div class="el-upload__text">请选择附件进行上传</div>
            </div>
          </el-upload>
          <div v-else class="view-file-list">
            <div v-if="fileList && fileList.length > 0" class="file-list">
              <div v-for="(file, index) in fileList" :key="index" class="file-item">
                <i class="el-icon-document"></i>
                <a :href="getFileUrl(file.url)" target="_blank" class="file-link">{{ file.name }}</a>
              </div>
            </div>
            <span v-else class="no-file">暂无附件</span>
          </div>
        </el-form-item>
      </el-form>
    </template>
    <template slot="footer">
      <el-button @click="handleClose">{{ isView ? '关闭' : '取消' }}</el-button>
      <el-button v-if="!isView" type="primary" @click="handleSubmit" :loading="loading">保存</el-button>
    </template>
  </myDialog>
</template>

<script>
import { addPolicy, updatePolicy } from '@/api/policy/management'
import { getListByDictType } from '@/api/sys/dictData'
import WangEditor from '@/components/wangEditor/index.vue'
import { uploadFileInfo } from '@/api/upload/upload'

export default {
  name: 'PolicyDialog',
  components: {
    WangEditor
  },
  props: {
    isDialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    isView: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        policyLabelList: [],
        fileName: '',
        referenceNumber: '',
        addType: 'direct', // 'direct' 或 'link'
        policyContent: '',
        link: '',
        attachmentList: []
      },
      selectedTagValues: [],
      availableTags: [],
      fileList: [],
      loading: false,
      uploading: false,
      selectKey: 0, // 用于强制重新渲染 el-select 的 key
      rules: {
        policyLabelList: [
          { required: true, message: '请选择政策标签', trigger: 'change', validator: (rule, value, callback) => {
            if (!this.selectedTagValues || this.selectedTagValues.length === 0) {
              callback(new Error('请至少选择一个政策标签'))
            } else {
              callback()
            }
          }}
        ],
        fileName: [
          { required: true, message: '请输入文件名称', trigger: 'blur' }
        ],
        referenceNumber: [
          { required: true, message: '请输入文号', trigger: 'blur' }
        ],
        addType: [
          { required: true, message: '请选择政策添加方式', trigger: 'change' }
        ],
        link: [
          { required: true, message: '请输入链接地址', trigger: 'blur', validator: (rule, value, callback) => {
            if (this.formData.addType == 'link') {
              if (!value || value.trim() == '') {
                callback(new Error('请输入链接地址'))
              } else {
                // URL 格式验证
                const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
                if (!urlPattern.test(value)) {
                  callback(new Error('请输入正确的链接地址'))
                } else {
                  callback()
                }
              }
            } else {
              callback()
            }
          }}
        ],
        policyContent: [
          { required: true, message: '请输入政策内容', trigger: 'blur', validator: (rule, value, callback) => {
            if (this.formData.addType === 'direct') {
              if (!value || value.trim() === '' || value === '<p><br></p>' || value === '<p></p>') {
                callback(new Error('请输入政策内容'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          }}
        ]
      }
    }
  },
  watch: {
    'formData.addType'(newVal, oldVal) {
      if (newVal !== oldVal && this.$refs.form) {
        // 清除之前的验证状态
        if (oldVal === 'direct') {
          this.$refs.form.clearValidate('policyContent')
        } else if (oldVal === 'link') {
          this.$refs.form.clearValidate('link')
        }
      }
    },
    editData: {
      handler(newVal) {
        if (newVal && (this.isEdit || this.isView) && Object.keys(newVal).length > 0) {
          // 根据 showType 判断添加方式：1=自定义文本(direct)，2=外部链接(link)
          const addType = newVal.showType === 2 ? 'link' : 'direct'
          
          // 处理附件：如果 filrUrl 是字符串，需要转换为数组
          let attachmentList = []
          if (newVal.attachmentList && Array.isArray(newVal.attachmentList)) {
            attachmentList = newVal.attachmentList
          } else if (newVal.filrUrl) {
            // 如果 filrUrl 是逗号分隔的字符串，转换为数组
            const fileUrls = typeof newVal.filrUrl === 'string' ? newVal.filrUrl.split(',').filter(url => url) : []
            attachmentList = fileUrls.map((url, index) => ({
              fileUrl: url.trim(),
              fileName: `附件${index + 1}`
            }))
          }
          
          this.formData = {
            id: newVal.id,
            policyLabelList: newVal.policyLabelList || [],
            fileName: newVal.fileName || '',
            referenceNumber: newVal.referenceNumber || '',
            addType: addType,
            policyContent: newVal.content || newVal.policyContent || '', // 内容字段可能是 content
            link: newVal.link || '',
            attachmentList: attachmentList
          }
          // 处理标签：支持 {labelCode, labelName} 或 {dictValue} 格式
          if (newVal.policyLabelList && Array.isArray(newVal.policyLabelList)) {
            this.selectedTagValues = newVal.policyLabelList.map(item => item.labelCode || item.dictValue || item)
          }
          // 处理附件显示
          if (attachmentList && attachmentList.length > 0) {
            this.fileList = attachmentList.map((item, index) => ({
              name: item.fileName || item.name || `附件${index + 1}`,
              url: item.fileUrl || item.url,
              uid: item.id || Date.now() + index
            }))
          }
        }
      },
      immediate: true,
      deep: true
    },
    isDialog(val) {
      if (!val) {
        this.resetForm()
      }
    }
  },
  created() {
    this.getPolicyTagList()
  },
  methods: {
    // 获取政策标签列表
    async getPolicyTagList() {
      const res = await getListByDictType('policy_info_label')
      if (res && res.code === 200 && res.data) {
        this.availableTags = res.data || []
      }
    },
    // 标签选择变化
    handleTagSelectChange(values) {
      // 确保值的唯一性和类型一致性（都是字符串）
      this.selectedTagValues = (values || []).map(v => String(v)).filter((v, index, self) => self.indexOf(v) === index)
      
      // 将选中的值转换为标签对象数组，格式为 [{labelCode, labelName}]
      this.formData.policyLabelList = this.selectedTagValues.map(value => {
        const tag = this.availableTags.find(item => String(item.dictValue) === String(value))
        if (tag) {
          return {
            labelCode: tag.dictValue, // 政策标签编号
            labelName: tag.dictLabel  // 政策标签名称
          }
        }
        return null
      }).filter(item => item !== null)
      
      // 强制更新 select 组件以解决 key 警告
      this.selectKey = Date.now()
      
      // 触发验证
      this.$refs.form && this.$refs.form.validateField('policyLabelList')
    },
    // 自定义上传
    async handleCustomUpload(options) {
      const { file, onSuccess, onError, onProgress } = options
      
      // 创建一个包含 abort 方法的对象，避免删除时的错误
      const requestObj = {
        abort: () => {
          // 空函数，避免删除时调用 abort 报错
        }
      }
      
      // 找到对应的文件项并更新状态为上传中
      let fileItem = this.fileList.find(item => {
        return item.uid === file.uid || 
               (item.raw && item.raw.uid === file.uid) ||
               (item.name === file.name && !item.status)
      })
      
      if (!fileItem) {
        // 如果找不到，创建一个新的文件项
        fileItem = {
          name: file.name,
          size: file.size,
          raw: file,
          uid: file.uid || Date.now() + Math.random(),
          status: 'uploading',
          percentage: 0
        }
        this.fileList.push(fileItem)
      } else {
        fileItem.status = 'uploading'
        fileItem.percentage = 0
      }
      
      try {
        this.uploading = true
        
        // 触发进度回调
        if (onProgress) {
          onProgress({ percent: 30 })
        }
        if (fileItem) {
          fileItem.percentage = 30
        }
        
        const res = await uploadFileInfo(file, 'baojiuye_policy_file')
        
        // 上传完成，更新进度
        if (onProgress) {
          onProgress({ percent: 100 })
        }
        if (fileItem) {
          fileItem.percentage = 100
        }
        
        if (res && res.code === 200) {
          if (!this.formData.attachmentList) {
            this.formData.attachmentList = []
          }
          
          // 根据实际返回的数据结构调整 - 可能需要根据实际 API 响应调整
          const fileInfo = {
            fileName: res.data?.fileName || res.data?.name || file.name,
            fileUrl: res.data?.fileUrl || res.data?.url || res.data?.path || res.data
          }
          
          // 检查是否已存在，避免重复添加
          const exists = this.formData.attachmentList.some(item => 
            item.fileName === fileInfo.fileName && item.fileUrl === fileInfo.fileUrl
          )
          if (!exists) {
            this.formData.attachmentList.push(fileInfo)
          }
          
          // 更新文件列表状态为成功
          if (fileItem) {
            fileItem.status = 'success'
            fileItem.url = fileInfo.fileUrl
            fileItem.response = res
            // 确保文件对象包含正确的信息
            if (!fileItem.name) fileItem.name = fileInfo.fileName
          }
          
          // 调用 onSuccess 回调，传入响应和文件对象
          // onSuccess 的参数应该是 (response, file, fileList)
          if (onSuccess) {
            onSuccess(res, fileItem || file, this.fileList)
          }
          
          this.$message.success('上传成功')
        } else {
          // 更新文件列表状态为失败
          if (fileItem) {
            fileItem.status = 'fail'
          }
          
          const errorMsg = res?.msg || '上传失败'
          this.$message.error(errorMsg)
          if (onError) {
            onError(new Error(errorMsg), fileItem || file, this.fileList)
          }
        }
      } catch (error) {
        console.error('上传失败', error)
        
        // 更新文件列表状态为失败
        if (fileItem) {
          fileItem.status = 'fail'
        }
        
        const errorMsg = error.message || '上传失败'
        this.$message.error(errorMsg)
        if (onError) {
          onError(error, fileItem || file, this.fileList)
        }
      } finally {
        this.uploading = false
      }
      
      // 返回包含 abort 方法的对象
      return requestObj
    },
    // 移除文件（仅前端删除）
    handleRemove(file, fileList) {
      // 从 formData.attachmentList 中移除
      if (this.formData.attachmentList) {
        const index = this.formData.attachmentList.findIndex(item => 
          item.fileUrl === file.url || item.fileName === file.name
        )
        if (index > -1) {
          this.formData.attachmentList.splice(index, 1)
        }
      }
      // 更新 fileList
      this.fileList = fileList || []
    },
    // 上传前验证
    beforeUpload(file) {
      const isValidSize = file.size / 1024 / 1024 < 50
      if (!isValidSize) {
        this.$message.error('上传文件大小不能超过 50MB!')
        return false
      }
      // 将文件添加到列表并设置为上传中状态
      const fileItem = {
        name: file.name,
        size: file.size,
        raw: file,
        uid: file.uid || Date.now() + Math.random(),
        status: 'uploading',
        percentage: 0
      }
      this.fileList.push(fileItem)
      return true
    },
    // 上传进度
    handleProgress(event, file) {
      // 更新文件的上传进度
      const fileItem = this.fileList.find(item => item.uid === file.uid)
      if (fileItem) {
        fileItem.percentage = event.percent || 0
      }
    },
    // 上传失败
    handleUploadError(err) {
      console.error('上传失败', err)
      this.$message.error('上传失败')
    },
    // 提交
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.loading = true
          
          // 构建符合后端要求的提交数据
          const submitData = {
            fileName: this.formData.fileName, // 文件名称
            referenceNumber: this.formData.referenceNumber, // 文号
            showType: this.formData.addType === 'direct' ? 1 : 2, // 展示类型 1自定义文本 2外部链接
            policyLabelList: this.formData.policyLabelList // 政策标签列表 [{labelCode, labelName}]
          }
          
          // 根据添加方式设置内容和链接
          if (this.formData.addType === 'direct') {
            // 直接输入内容
            submitData.content = this.formData.policyContent // 内容
          } else {
            // 外部链接
            submitData.link = this.formData.link // 链接
          }
          
          // 处理附件：将 attachmentList 中的 fileUrl 拼接成字符串
          if (this.formData.attachmentList && this.formData.attachmentList.length > 0) {
            const fileUrls = this.formData.attachmentList
              .map(item => item.fileUrl || item.url)
              .filter(url => url) // 过滤空值
            
            if (fileUrls.length > 0) {
              submitData.filrUrl = fileUrls.join(',') // 多个附件用逗号拼接
            }
          }
          
          // 如果是编辑，需要传递 id（如果有 code 字段也需要传递）
          if (this.isEdit && this.formData.id) {
            submitData.id = this.formData.id
          }

          const api = this.isEdit ? updatePolicy : addPolicy
          api(submitData).then(res => {
            if (res && res.code === 200) {
              this.$message.success(this.isEdit ? '编辑成功' : '新增成功')
              this.$emit('success')
              this.handleClose()
            } else {
              this.$message.error(res.msg || '操作失败')
            }
          }).catch(err => {
            console.error('请求出错', err)
            this.$message.error('操作失败')
          }).finally(() => {
            this.loading = false
          })
        }
      })
    },
    // 关闭
    handleClose() {
      this.$emit('handleClose')
      this.resetForm()
    },
    // 重置表单
    resetForm() {
      this.$refs.form && this.$refs.form.resetFields()
      this.formData = {
        policyLabelList: [],
        fileName: '',
        referenceNumber: '',
        addType: 'direct',
        policyContent: '',
        link: '',
        attachmentList: []
      }
      this.selectedTagValues = []
      this.fileList = []
    },
    // 获取附件完整URL（拼接前缀）
    getFileUrl(url) {
      if (!url) return ''
      // 如果已经是完整URL（以http://或https://开头），直接返回
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      // 否则拼接imageUrlprefix前缀
      return (this.imageUrlprefix || '') + url
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-area {
  width: 100%;
  padding: 40px;
  text-align: center;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    border-color: #409eff;
  }
  
  .el-upload__text {
    margin-top: 8px;
    color: #606266;
    font-size: 14px;
  }
}

.view-content {
  min-height: 400px;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
}

.view-file-list {
  .file-list {
    .file-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #ebeef5;
      
      &:last-child {
        border-bottom: none;
      }
      
      .el-icon-document {
        font-size: 18px;
        color: #409eff;
        margin-right: 8px;
      }
      
      .file-link {
        color: #409eff;
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
  
  .no-file {
    color: #909399;
    font-size: 14px;
  }
}

/deep/ .el-form-item__label {
  .el-form-item.is-required &::before {
    content: '*';
    color: #f56c6c;
    margin-right: 4px;
  }
}

/deep/ .el-form-item__label {
    color: #2A3347 !important;
    font-weight: 400;
    font-size: 16px;
}

/deep/ .el-dialog__header {
    background: #297DEE !important;
   
}

/deep/ .el-dialog__title {
    color: #ffffff !important;
    font-weight: 700;
    font-size: 17px;
}

/deep/ .el-dialog__headerbtn {
  filter: brightness(0) invert(1) !important;
}
</style>

